import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import FullWidthContainer from 'components/consumer/FullWidthContainer';
import CtaButton from 'components/essentials/Cta/CtaButton';
import CtaLink from 'components/essentials/Cta/CtaLink';
import useUserData from 'data-hooks/useUserData';
import CustomerDashboardNavLinks from './components/CustomerDashboardNavLinks';
import CustomerDashboardBreadcrumb from './components/CustomerDashboardBreadcrumb';
import { navLinks } from './customerDashboardLinks';

const CustomerDashboardPageTemplateUI = ({
  children,
  email,
  onLogoutClick,
  parentPageItem,
  showBreadcrumb = true,
  showSidebar = true,
  showTitle = true,
  title,
  titleComponent,
  variant,
}) => {
  const { isLoggedIn } = useUserData();
  const parentPageBreadcrumbArray = parentPageItem ? [parentPageItem] : [];
  const currentPageBreadcrumbArray = title
    ? [
        {
          label: title,
          link: '.',
        },
      ]
    : [];

  return (
    <FullWidthContainer className="flex justify-start relative w-full xl:min-h-[calc(100vh_-_3.75rem_-_36px_-_var(--visible-promotion-banner-height)_)]">
      {showSidebar && (
        <div className="bg-gray-light9 hidden w-[300px] xl:flex xl:flex-col xl:justify-between">
          <div className="pt-[60px] sticky top-0">
            <CustomerDashboardNavLinks items={navLinks} />

            <div className="bg-gray-light9 mt-10 pt-0 pr-6 pb-8 pl-[52px]">
              <div className="text-sm text-gray-light1 mb-2">
                Have a question or issue?
              </div>

              <CtaLink
                to="/contact-us/"
                styling="solid-button"
                color="brand"
                size="sm"
              >
                Contact us
              </CtaLink>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto my-0 max-w-[1100px] px-0 py-2.5 w-full lg:px-0 lg:py-[55px] xl:px-0 xl:py-[65px] lg:pl-[30px] lg:pr-[30px] xl:m-0 xl:pl-[60px] xl:pr-[60px]">
        {showBreadcrumb && (
          <CustomerDashboardBreadcrumb
            items={[
              ...parentPageBreadcrumbArray,
              ...currentPageBreadcrumbArray,
            ]}
          />
        )}
        {showTitle && titleComponent ? (
          <h1 className="text-[clamp(1.75rem,5.2vw,2.5rem)] leading-[clamp(2.25rem,6.25vw,3rem)] tracking-[.0125rem] font-bold text-gray mt-0 mx-0 mb-6 max-[1023px]:px-4 max-[1023px]:py-0">
            {titleComponent || title}
          </h1>
        ) : showTitle && title ? (
          <h1 className="text-[clamp(1.75rem,5.2vw,2.5rem)] leading-[clamp(2.25rem,6.25vw,3rem)] tracking-[.0125rem] font-bold text-gray mt-0 mx-0 mb-6 max-[1023px]:px-4 max-[1023px]:py-0">
            {titleComponent || title}
          </h1>
        ) : null}

        <div
          className={classNames(
            'flex flex-col mt-0 mx-4 mb-6 lg:flex-row lg:m-0',
            {
              'max-[767px]:ml-0 max-[767px]:mr-0':
                variant === 'BuildUpdatesPage',
            }
          )}
        >
          {children}
        </div>

        <div className="text-base [border-top:1px_solid_#e5e5e5] text-gray-light1 flex mt-20 pt-10 flex-col max-[1023px]:text-sm max-[1023px]:[border-top:0] max-[1023px]:mt-2 max-[1023px]:mb-2 max-[1023px]:pt-0 max-[1023px]:pl-4 max-[1023px]:pr-4">
          {isLoggedIn && (
            <span className="max-[1023px]:items-center max-[1023px]:[border-top:1px_solid_#f1f1f1] max-[1023px]:flex max-[1023px]:justify-between first:max-[1023px]:[border-top:0]">
              <span>Logged in as {email}</span>

              <span className="max-[1023px]:hidden"> &nbsp;&bull;&nbsp; </span>

              <CtaButton
                hasArrow={false}
                noLinkPadding
                color="brand"
                size="sm"
                styling="link"
                onClick={onLogoutClick}
              >
                Log out
              </CtaButton>
            </span>
          )}

          <span className="max-[1023px]:items-center max-[1023px]:[border-top:1px_solid_#f1f1f1] max-[1023px]:flex max-[1023px]:justify-between first:max-[1023px]:[border-top:0] md:hidden">
            <span>Have a question or issue?</span>

            <a
              className="text-base font-bold text-brand !no-underline whitespace-nowrap max-[1023px]:block max-[1023px]:pt-4 max-[1023px]:pr-0 max-[1023px]:pb-4 max-[1023px]:pl-4 hover:!no-underline hover:cursor-pointer"
              href="/contact-us/"
            >
              Contact us
            </a>
          </span>
        </div>
      </div>
    </FullWidthContainer>
  );
};

CustomerDashboardPageTemplateUI.propTypes = {
  children: PropTypes.node,
  email: PropTypes.string,
  onLogoutClick: PropTypes.func,
  parentPageItem: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
  }),
  showBreadcrumb: PropTypes.bool,
  showTitle: PropTypes.bool,
  title: PropTypes.string,
  titleComponent: PropTypes.string,
  showSidebar: PropTypes.bool,
  variant: PropTypes.oneOf(['BuildUpdatesPage']),
};

export default CustomerDashboardPageTemplateUI;
